/**
 * Data-bearing lists. These are server-driven: ranking, filtering, grouping and
 * paging happen on the server; the component renders the page it was handed and
 * asks for the next one. None accepts a plain in-memory array as its data model
 * (UX-004 §4 rule; UX-002 §9 scale rules). Rows are virtualised so the browser
 * never holds more than a window.
 */
import { useRef, type ReactNode } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { Figure } from "@solarkey/api-client";
import { Metric } from "./metric";

export type { ColumnDef };

/** A page-driven source: the rows loaded so far, plus how to get more. */
export interface PagedSource {
  hasNextPage?: boolean;
  isFetching?: boolean;
  /** Fetch the next cursor page (server-side). Called on scroll near the end. */
  fetchNextPage?: () => void;
}

export interface DataTableProps<T> extends PagedSource {
  columns: ColumnDef<T, unknown>[];
  rows: T[];
  getRowId: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  /** Row height estimate for virtualisation; density comes from tokens. */
  estimateRowHeight?: number;
  /** Shown when there are no rows and nothing is loading (caller distinguishes
   *  verified-quiet from no-data). */
  emptyState?: ReactNode;
  maxHeight?: number | string;
}

export function DataTable<T>(props: DataTableProps<T>): ReactNode {
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable<T>({
    data: props.rows,
    columns: props.columns,
    getRowId: props.getRowId,
    getCoreRowModel: getCoreRowModel(),
    // Server owns sort/filter/group/paging.
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
  });

  const tableRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => props.estimateRowHeight ?? 40,
    overscan: 10,
  });

  const onScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    if (!props.hasNextPage || props.isFetching || !props.fetchNextPage) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      props.fetchNextPage();
    }
  };

  const virtualItems = rowVirtualizer.getVirtualItems();
  const isEmpty = tableRows.length === 0 && !props.isFetching;

  return (
    <div className="sk-table" role="table">
      <div className="sk-table__head" role="rowgroup">
        {table.getHeaderGroups().map((hg) => (
          <div className="sk-table__row sk-table__row--head" role="row" key={hg.id}>
            {hg.headers.map((header) => (
              <div className="sk-table__cell sk-table__cell--head" role="columnheader" key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div
        ref={scrollRef}
        className="sk-table__body"
        role="rowgroup"
        onScroll={onScroll}
        style={{ maxHeight: props.maxHeight ?? 520 }}
      >
        {isEmpty ? (
          <div className="sk-table__empty">{props.emptyState ?? null}</div>
        ) : (
          <div
            style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}
          >
            {virtualItems.map((vi) => {
              const row = tableRows[vi.index];
              if (!row) return null;
              return (
                <div
                  key={row.id}
                  className="sk-table__row"
                  role="row"
                  data-clickable={props.onRowClick ? "true" : undefined}
                  tabIndex={props.onRowClick ? 0 : undefined}
                  onClick={props.onRowClick ? () => props.onRowClick?.(row.original) : undefined}
                  onKeyDown={
                    props.onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            props.onRowClick?.(row.original);
                          }
                        }
                      : undefined
                  }
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: vi.size,
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <div className="sk-table__cell" role="cell" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
        {props.isFetching ? <div className="sk-table__loading">Loading…</div> : null}
      </div>
    </div>
  );
}

/* ── RankedList ─────────────────────────────────────────────────────────────
   Value-at-stake order (server-ranked) with a centred deviation bar. The first
   row is the one that costs most (UX-003 principle 3). */
export interface RankedItem {
  id: string;
  title: string;
  subtitle?: string;
  /** The figure at stake, rendered via Metric (basis/state preserved). */
  figure?: Figure;
  /** Deviation from the group median, in percent (−100…100), for the bar. */
  deviationPct?: number;
}

export function RankedList(props: {
  items: RankedItem[];
  onSelect?: (id: string) => void;
  emptyState?: ReactNode;
}): ReactNode {
  if (props.items.length === 0) return <>{props.emptyState ?? null}</>;
  return (
    <ul className="sk-ranked" role="list">
      {props.items.map((item) => {
        const dev = clampDev(item.deviationPct);
        return (
          <li className="sk-ranked__row" key={item.id}>
            <button
              type="button"
              className="sk-ranked__hit"
              onClick={props.onSelect ? () => props.onSelect?.(item.id) : undefined}
            >
              <span className="sk-ranked__labels">
                <span className="sk-ranked__title">{item.title}</span>
                {item.subtitle ? (
                  <span className="sk-ranked__subtitle">{item.subtitle}</span>
                ) : null}
              </span>
              {item.figure ? (
                <Metric figure={item.figure} showFreshness={false} />
              ) : null}
              {typeof dev === "number" ? (
                <span
                  className="sk-ranked__dev"
                  aria-label={`Deviation ${dev > 0 ? "+" : ""}${dev}%`}
                >
                  <span className="sk-ranked__dev-centre" aria-hidden="true" />
                  <span
                    className="sk-ranked__dev-fill"
                    data-dir={dev >= 0 ? "pos" : "neg"}
                    style={devBarStyle(dev)}
                    aria-hidden="true"
                  />
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/* ── CardGrid ───────────────────────────────────────────────────────────────
   A responsive grid of cards (same server-page model; the caller renders each). */
export function CardGrid<T>(props: {
  items: T[];
  getItemKey: (item: T, index: number) => string;
  renderCard: (item: T) => ReactNode;
  minColumnWidth?: number;
  emptyState?: ReactNode;
}): ReactNode {
  if (props.items.length === 0) return <>{props.emptyState ?? null}</>;
  return (
    <div
      className="sk-cardgrid"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${props.minColumnWidth ?? 240}px, 1fr))`,
      }}
    >
      {props.items.map((item, i) => (
        <div className="sk-cardgrid__cell" key={props.getItemKey(item, i)}>
          {props.renderCard(item)}
        </div>
      ))}
    </div>
  );
}

function clampDev(v: number | undefined): number | undefined {
  if (typeof v !== "number") return undefined;
  return Math.max(-100, Math.min(100, v));
}

function devBarStyle(dev: number): React.CSSProperties {
  const half = Math.abs(dev) / 2; // percent of full width on one side of centre
  return dev >= 0
    ? { left: "50%", width: `${half}%` }
    : { right: "50%", width: `${half}%` };
}
